import {
  Authenticated,
  IResourceComponentsProps,
  useTranslate,
} from "@refinedev/core";
import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import { Button, Card, Typography, Tooltip, Alert, Flex } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { useAntTheme } from "hooks/useAntTheme";
import { useClipboard } from "hooks/useClipboard";
import { useProjectDetails } from "./hooks/useProjectDetails";
import { isProjectHeyGenius } from "./helpers/helper";
import { SubPageHeader } from "components/SubPageHeader/";
import { LoadingSpinner } from "components/LoadingSpinner/";
import { HG_EDITOR_SCRIPT_URL } from "../constants";
import { ReservedProjectTags } from "./types";


export const HeyGeniusInstallPage: React.FC<IResourceComponentsProps> = () => {
  const t = useTranslate();
  const params = useParams();
  const { theme } = useAntTheme();
  const { copy } = useClipboard();
  const projectId = params.id;

  const queryResult = useProjectDetails({ projectId });
  const { data } = queryResult;
  const project = data?.data;

  // Get the first storyboard for language
  const storyboard = project?.storyboards?.[0];
  const snippet = useMemo(
    () =>
      generatesSnippetForHeyGenius(
        project?.organization_id,
        storyboard?.language,
        project?.tags.find((tag) => tag.tag_key === ReservedProjectTags.PAGE_ID)
          ?.tag_value,
        project?.tags.find(
          (tag) => tag.tag_key === ReservedProjectTags.PRODUCT_SKU
        )?.tag_value
      ),
    [project?.organization_id, storyboard?.language, project?.tags]
  );

  const handleCopyToClipboard = () => {
    copy(snippet);
  };

  if (!project) return <LoadingSpinner />;
  return (
    <Authenticated key="heygenius_install">
      <SubPageHeader
        itemId={project.id}
        itemTitle={project.title}
        page="heygeniusInstall"
        source="projects"
        spaced
      />
      {project && isProjectHeyGenius(project?.type) && (
        <Flex vertical gap={24}>
          <Typography.Title level={2} style={{ margin: 0 }}>
            {t("pages.projects.heygenius.installTitle")}
          </Typography.Title>

          <Typography.Paragraph>
            {t("pages.projects.heygenius.installDescription")}
          </Typography.Paragraph>

          <Card
            title={t("pages.projects.detail.heygSnippetTitle")}
            type="inner"
            styles={{
              body: {
                fontFamily: "monospace",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                fontSize: 12,
              },
            }}
            extra={
              <Tooltip title={t("buttons.copyToClipboard")}>
                <Button
                  type="text"
                  size="large"
                  icon={<CopyOutlined style={{ color: theme.colorText }} />}
                  onClick={handleCopyToClipboard}
                ></Button>
              </Tooltip>
            }
          >
            {snippet}
          </Card>
          <Alert
            type="warning"
            showIcon
            message={t("pages.projects.heygenius.installWarningNonPublished")}
          />

          <div>
            <Typography.Title level={3} style={{ margin: 0 }}>
              {t("pages.projects.heygenius.installInstructions")}
            </Typography.Title>
            <Typography.Paragraph>
              <ol>
                <li>{t("pages.projects.heygenius.step1")}</li>
                <li>{t("pages.projects.heygenius.step2")}</li>
                <li>{t("pages.projects.heygenius.step3")}</li>
                <li>{t("pages.projects.heygenius.step4")}</li>
              </ol>
            </Typography.Paragraph>
          </div>
        </Flex>
      )}
    </Authenticated>
  );
};

function generatesSnippetForHeyGenius(
  organizationId: string,
  language = "en-US",
  pageId: string,
  productSku?: string
) {
  return `<script>
  window.heygeniusSettings = {
    organization_id: '${organizationId}',
    ${productSku ? `product_sku: '${productSku}'` : `page_id: '${pageId}'`},
    language: '${language}',
  };
${script}
</script>
`;
}
export const script = `(function(){var w=window;if(typeof w.HeyGenius==="function"){w.HeyGenius("update",w.heygeniusSettings);return}w.HeyGenius=function(){w.HeyGenius.q=w.HeyGenius.q||[];w.HeyGenius.q.push(arguments)};var loadScript=function(){var s=document.createElement("script");s.async=true;s.src=(w.heygeniusSettings&&w.heygeniusSettings.widget_base||"${HG_EDITOR_SCRIPT_URL}")+"/widget.js";document.head.appendChild(s)};document.readyState==="complete"?loadScript():w.addEventListener("load",loadScript)})();`;
